import React, { FC, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { PageSpinner } from '@td-client-frontend/common';
import {
  MarketingComment,
  MarketingCommentsPage,
  useGetMarketingComments,
  useMarketingCommentsMutation
} from '../../../../../hooks/useMarketingComments';
import CommentsTextArea from './CommentsTextArea';
import { useAuth } from '../../../../../context/AuthContext';
import { PermMode } from '../../../../WithPermission';
import { InfiniteData, useQueryClient } from 'react-query';
import { MarketingProfileById } from '../../../../../@types/Marketing';
import MarketingCommentItem from './MarketingCommentItem';
import { Nullable } from '../../../../../@types/common';
import { Permissions } from '../../../../../@types/Permissions';

interface Props {
  profileData: MarketingProfileById;
  onTotalCommentsChange: (totalComments: number) => void;
}

export const MarketingComments: FC<Props> = ({ profileData, onTotalCommentsChange }) => {
  const { checkPerm } = useAuth();
  const queryCache = useQueryClient();

  const [commentToEdit, setCommentToEdit] = useState<Nullable<MarketingComment>>(null);

  const handleCommentsUpdate = (data: InfiniteData<MarketingCommentsPage>) => {
    onTotalCommentsChange(data.pages[0]?.totalComments);
  };

  const { data, isFetching, isFetchingNextPage, refetch, fetchNextPage } = useGetMarketingComments({
    profileId: profileData?.id,
    onSuccess: handleCommentsUpdate
  });
  const { addMarketingComment, updateMarketingComment, deleteMarketingComment, mutationLoading } =
    useMarketingCommentsMutation(profileData!.id);

  const fetchMoreComments = () => fetchNextPage();

  const canAddComment: boolean = checkPerm([Permissions.MarketingProfileCommentsWrite], PermMode.ALL);

  const isLoading: boolean = useMemo(() => {
    return (mutationLoading || isFetching) && !isFetchingNextPage;
  }, [isFetching, mutationLoading, isFetchingNextPage]);

  const onAddMarketingComment = (commentValue: string) => {
    addMarketingComment(commentValue);
  };

  const onCommentUpdate = (commentValue: string) => {
    if (commentToEdit) updateMarketingComment({ commentId: commentToEdit?.id, commentValue });
    setCommentToEdit(null);
  };

  const commentsLength: number = useMemo(() => {
    if (data) {
      return data?.pages.reduce((acc: number, page: MarketingCommentsPage) => {
        return acc + page.marketingCommentsGroup?.length;
      }, 0);
    }
    return 0;
  }, [data]);

  useEffect(() => {
    if (!mutationLoading) {
      refetch();
    }
  }, [mutationLoading, profileData.id, queryCache, refetch]);

  return (
    <div data-testid="marketing-comments">
      {canAddComment && (
        <CommentsTextArea
          onChange={onAddMarketingComment}
          rightButtonLabel="Add comment"
          textAreaLable="Comment"
          textAreaName="add-marketing-comment"
          isAddComment
        />
      )}
      {!data?.pages.length || isLoading ? (
        <div className="pt-2" data-testid="comments-spinner">
          <PageSpinner />
        </div>
      ) : (
        <div className="w-full" data-testid="marketing-comments-list">
          {data.pages.map((page) =>
            page.marketingCommentsGroup.map((marketingComment: MarketingComment) => (
              <MarketingCommentItem
                key={marketingComment.id}
                marketingComment={marketingComment}
                setCommentToEdit={setCommentToEdit}
                onChange={onCommentUpdate}
                onDelete={deleteMarketingComment}
                commentToEdit={commentToEdit}
              />
            ))
          )}
          <div
            className="flex w-full py-6 border-grey-20 font-effra text-gray-45 text-lg"
            data-testid="marketing-comments-list-footer"
          >
            {!profileData.totalComments ? (
              <p>Added comments will appear here.</p>
            ) : (
              <div
                className="flex w-full font-effra text-gray-45 text-lg relative h-8"
                data-testid="marketing-comments-viewing"
              >
                <p className="absolute">
                  Viewing {commentsLength} of {profileData.totalComments} comments
                </p>
                <div className="flex w-full items-center justify-center">
                  {isFetchingNextPage ? (
                    <div className="pt-2 w-5" data-testid="load-more-spinner">
                      <PageSpinner />
                    </div>
                  ) : (
                    <button
                      onClick={fetchMoreComments}
                      className={clsx('text-blue-80', profileData.totalComments === commentsLength && 'hidden')}
                    >
                      Load more
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
